import { StatsigProvider, useGate } from "statsig-react";
import Statsig from "statsig-js";
import Routes from "./routes/routes";
import useInternetStatus from "./custom-hooks/useInternetStatus";
import Notification from "./components/Notification";
import { useDispatch, useSelector } from "react-redux";
import { useApiInterceptor, useChatApiInterceptor } from "./api/api-config";
import { useEffect, useState, useCallback } from "react";
import { LicenseManager } from "ag-grid-enterprise";
import ChatDQP from './components/ChatDQP/ChatDQP';
import FloatingChatButton from "./components/ChatDQP/FloatingChatButton";
import useChatDQP from "./custom-hooks/useChatDQP";
import { useLocation } from "react-router-dom";

const STATSIG_CLIENT_KEY = "client-8v4qg1jXt2Vau1qZ2W9pbk94qkS4Our0xqH1a1YPZeT";

function App() {
  const [isReady, setIsReady] = useState(false);
  const dispatch = useDispatch();
  useApiInterceptor();
  useChatApiInterceptor();
  useEffect(() => {
    const initializeStatsig = async () => {
      try {
        localStorage.removeItem("statsig-overrides"); // Remove old overrides

        await Statsig.initialize(STATSIG_CLIENT_KEY);
        setIsReady(true);
      } catch (err) {
        setIsReady(true);
      }
    };

    initializeStatsig();
  }, []);

  if (!isReady) {
    return <div></div>;
  }

  return (
    <StatsigProvider sdkKey={STATSIG_CLIENT_KEY} waitForInitialization={true}>
      <AppContent />
    </StatsigProvider>
  );
}

function AppContent() {
  const diligentGate = useGate("diligent");
  const isDiligentEnabled = diligentGate.value;
  const { isOnline, message } = useInternetStatus();
  const [show, setShow] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const { setOpen } = useChatDQP();
  const location = useLocation();
  const loggedInUserDetails = useSelector((state) => state.loggedInUser?.userDetails);

  // Show the floating chat button only on allowed routes AND when user has Chat Diligent access (admin grants it per sponsor user; default off)
  const chatButtonPaths = [
    "/TrialExperience",
    "/Providers",
    "/Documents",
    "/RFI/History",
    "/documents/RFI",
    "/Documents/Audit",
  ];
  const isAllowedPath = chatButtonPaths.some(
    (path) =>
      location?.pathname === path || location?.pathname?.startsWith(path + "/")
  );
  const hasChatDiligentAccess = loggedInUserDetails?.chat_diligent_access === true;
  const shouldShowFloatingChatButton = isAllowedPath && hasChatDiligentAccess;

  // 🔄 Function to Force Fetch Latest Feature Gates
  const fetchLatestStatsigValues = useCallback(async () => {
    try {
      await Statsig.updateUser({});
    } catch (err) {
      console.error("❌ Failed to refresh Statsig config:", err);
    }
  }, []);

  // ✅ Fetch the latest values when the app loads
  useEffect(() => {
    fetchLatestStatsigValues();
  }, [fetchLatestStatsigValues]);

  // ✅ Refresh Config When Internet Comes Online
  useEffect(() => {
    if (message) {
      setErrorMessage(
        isOnline
          ? "Internet connection restored"
          : "Internet connection disconnected"
      );
      setShow(true);

      if (isOnline) {
        fetchLatestStatsigValues();
      }
    }
  }, [message, isOnline, fetchLatestStatsigValues]);
  
  // LicenseManager.setLicenseKey(process.env.REACT_APP_AG_GRID_LICENSE_KEY || "Using_this_{AG_Grid}_Enterprise_key_{AG-087108}_in_excess_of_the_licence_granted_is_not_permitted___Please_report_misuse_to_legal@ag-grid.com___For_help_with_changing_this_key_please_contact_info@ag-grid.com___{Diligent_Pharma}_is_granted_a_{Single_Application}_Developer_License_for_the_application_{DQP_2.0_Prototype}_only_for_{1}_Front-End_JavaScript_developer___All_Front-End_JavaScript_developers_working_on_{DQP_2.0_Prototype}_need_to_be_licensed___{DQP_2.0_Prototype}_has_not_been_granted_a_Deployment_License_Add-on___This_key_works_with_{AG_Grid}_Enterprise_versions_released_before_{28_April_2026}____[v3]_[01]_MTc3NzMzMDgwMDAwMA==ff8f1686d2fd2662a610a2a1369517a9");
  LicenseManager.setLicenseKey(process.env.REACT_APP_AG_GRID_LICENSE_KEY || "Using_this_{AG_Grid}_Enterprise_key_{AG-112849}_in_excess_of_the_licence_granted_is_not_permitted___Please_report_misuse_to_legal@ag-grid.com___For_help_with_changing_this_key_please_contact_info@ag-grid.com___{Diligent_Pharma}_is_granted_a_{Single_Application}_Developer_License_for_the_application_{DQP_2.0_Prototype}_only_for_{1}_Front-End_JavaScript_developer___All_Front-End_JavaScript_developers_working_on_{DQP_2.0_Prototype}_need_to_be_licensed___{DQP_2.0_Prototype}_has_been_granted_a_Deployment_License_Add-on_for_{1}_Production_Environment___This_key_works_with_{AG_Grid}_Enterprise_versions_released_before_{28_April_2026}____[v3]_[01]_MTc3NzMzMDgwMDAwMA==e1e7416cec010948ffbad1ffcfc3fc71");

  return (
    <div className="App">
      <Routes />
      <button onClick={fetchLatestStatsigValues} className=""></button>
      <ChatDQP />

      {shouldShowFloatingChatButton && (
        <FloatingChatButton onClick={() => setOpen(true)} />
      )}
      <Notification
        show={show}
        setShow={setShow}
        status={isOnline ? "success" : "error"}
        errorMessage={errorMessage}
      />
    </div>
  );
}

export default App;
